import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CircularProgress } from "@mui/material";

import { Color } from "../../../components/Form/Button";
import AgentService from "Services/agent.service";
import { AppPageCardInform } from "Components/AppPageCardInform";
import { EmojiSad as EmojiSadIcon } from "iconsax-react"

type RejectShipeeziNetworkInvitePayload = {
  token: string;
};

export const RejectShipeeziNetworkInvite = () => {
  const { t: translate } = useTranslation();

  // eslint-disable-next-line no-restricted-globals
  const parsed: { token?: string } = queryString.parse(location.search);

  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [actionType, setActionType] = useState<"accept" | "decline" | null>(null);

  // Mutation para rejeitar o convite
  const rejectMutation = useMutation({
    mutationFn: (payload: RejectShipeeziNetworkInvitePayload) =>
      AgentService.rejectInvite(payload),
    onSuccess: () => {
      setActionCompleted(true);
      setActionType("decline");
    },
    onError: (error) => {
      console.error("Erro ao rejeitar convite:", error);
    },
  });

  // Função para executar a rejeição automaticamente ao carregar a página
  useEffect(() => {
    if (parsed?.token && !actionCompleted) {
      handleReject();
    }
  }, [parsed?.token, actionCompleted]);

  const handleReject = () => {
    if (!parsed?.token) return;
    
    rejectMutation.mutate({
      token: parsed.token,
    });
  };

  const handleCloseWindow = () => {
    window.close();
  };

  const getContentProps = () => {
    if (rejectMutation.isLoading) {
      return {
        icon: <CircularProgress />,
        title: translate("loading")
      };
    }

    if (rejectMutation.isError) {
      const error = rejectMutation.error as any;
      let errorMessage = translate("anErrorOccurredWhileProcessingYourRejectionPleaseTryAgainOrContactSupport");
      let errorTitle = translate("errorProcessingRejection");

      if (error?.response?.status === 400 || error?.response?.status === 401) {
        errorTitle = translate("invalidOrExpiredLink");
        errorMessage = translate("thisInvitationLinkIsInvalidOrHasAlreadyExpiredPleaseContactUsToReceiveANewInvitation");
      }

      return {
        icon: <EmojiSadIcon size={32} />,
        title: errorTitle,
        content: errorMessage,
        actionText: translate("closeWindow"),
        onActionClick: handleCloseWindow,
        actionColor: Color.SECONDARY_DARK
      };
    }

    // Estado de sucesso
    if (actionCompleted && actionType === "decline") {
      return {
        icon: <EmojiSadIcon size={32} />,
        title: translate("youHaventAcceptedTheInvitation"),
        content: translate("youCanSafelyCloseThisWindowNowIfYouNeedAnyFurtherAssistanceFeelFreeToReachOutHaveAGreatDay"),
        actionText: translate("closeWindow"),
        onActionClick: handleCloseWindow,
        actionColor: Color.SECONDARY_DARK
      };
    }

    // Estado sem token
    if (!parsed?.token) {
      return {
        icon: <EmojiSadIcon size={32} />,
        title: translate("invalidLink"),
        content: translate("thisLinkIsNotValidPleaseCheckIfYouHaveAccessedTheCorrectLink"),
        actionText: translate("closeWindow"),
        onActionClick: handleCloseWindow,
        actionColor: Color.SECONDARY_DARK
      };
    }

    // Estado inicial
    return {
      icon: <CheckCircleIcon sx={{ color: "#E6EEF6", fontSize: 72 }} />,
      title: translate("loading")
    };
  };

  return <AppPageCardInform {...getContentProps()} />;
};